import { doc, setDoc, deleteDoc, onSnapshot, collection, query, where, Timestamp, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { UserRole } from '../lib/types/auth';

export interface TypingUser {
  userId: string;
  userEmail: string;
  displayName: string;
  startedTyping: Timestamp;
  lastActivity: Timestamp;
  draftContent?: string; // The actual message content being typed
  cursorPosition?: number; // Optional: cursor position for advanced features
}

class TypingIndicatorService {
  private typingTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private readonly TYPING_TIMEOUT = 15000; // 15 seconds
  private readonly CLEANUP_TIMEOUT = 20000; // 20 seconds for cleanup

  /**
   * Check if user is Master Admin
   */
  private async isMasterAdmin(userEmail: string): Promise<boolean> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', userEmail));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        return userData.roles?.includes(UserRole.MasterAdmin) || false;
      }
      return false;
    } catch (error) {
      console.error('Error checking Master Admin status:', error);
      return false;
    }
  }

  /**
   * Indicate that a user is typing in a chat with draft content
   */
  async startTyping(chatId: string, userId: string, userEmail: string, displayName: string, draftContent?: string): Promise<void> {
    console.log('🚀 Starting typing indicator:', {
      chatId,
      userId,
      userEmail,
      displayName,
      draftContent
    });
    
    try {
      const typingRef = doc(db, 'chats', chatId, 'typing', userId);
      const typingData = {
        userId,
        userEmail,
        displayName,
        startedTyping: Timestamp.now(),
        lastActivity: Timestamp.now(),
        ...(draftContent !== undefined && { draftContent })
      };
      
      console.log('💾 Saving typing data to Firestore:', typingData);
      await setDoc(typingRef, typingData);
      console.log('✅ Typing data saved successfully');

      // Clear existing timeout for this user
      const timeoutKey = `${chatId}-${userId}`;
      if (this.typingTimeouts.has(timeoutKey)) {
        clearTimeout(this.typingTimeouts.get(timeoutKey)!);
      }

      // Set new timeout to automatically stop typing
      const timeout = setTimeout(() => {
        console.log('⏰ Typing timeout reached, stopping typing for:', userId);
        this.stopTyping(chatId, userId);
      }, this.TYPING_TIMEOUT);

      this.typingTimeouts.set(timeoutKey, timeout);
    } catch (error) {
      console.error('❌ Error starting typing indicator:', error);
    }
  }

  /**
   * Update typing activity with draft content (call this on every character change)
   */
  async updateTypingActivity(chatId: string, userId: string, draftContent?: string): Promise<void> {
    console.log('🔄 Updating typing activity:', {
      chatId,
      userId,
      draftContent
    });
    
    try {
      const typingRef = doc(db, 'chats', chatId, 'typing', userId);
      const updateData = {
        lastActivity: Timestamp.now(),
        ...(draftContent !== undefined && { draftContent })
      };
      
      console.log('💾 Updating typing data in Firestore:', updateData);
      await setDoc(typingRef, updateData, { merge: true });
      console.log('✅ Typing data updated successfully');

      // Reset timeout
      const timeoutKey = `${chatId}-${userId}`;
      if (this.typingTimeouts.has(timeoutKey)) {
        clearTimeout(this.typingTimeouts.get(timeoutKey)!);
      }

      const timeout = setTimeout(() => {
        console.log('⏰ Typing update timeout reached, stopping typing for:', userId);
        this.stopTyping(chatId, userId);
      }, this.TYPING_TIMEOUT);

      this.typingTimeouts.set(timeoutKey, timeout);
    } catch (error) {
      console.error('❌ Error updating typing activity:', error);
    }
  }

  /**
   * Stop typing indicator for a user
   */
  async stopTyping(chatId: string, userId: string): Promise<void> {
    try {
      const typingRef = doc(db, 'chats', chatId, 'typing', userId);
      await deleteDoc(typingRef);

      // Clear timeout
      const timeoutKey = `${chatId}-${userId}`;
      if (this.typingTimeouts.has(timeoutKey)) {
        clearTimeout(this.typingTimeouts.get(timeoutKey)!);
        this.typingTimeouts.delete(timeoutKey);
      }
    } catch (error) {
      console.error('Error stopping typing indicator:', error);
    }
  }

  /**
   * Listen to typing indicators for a chat (Master Admin only)
   */
  async subscribeToTypingIndicators(
    chatId: string, 
    currentUserId: string,
    currentUserEmail: string,
    onTypingUsersChange: (typingUsers: TypingUser[]) => void
  ): Promise<(() => void) | null> {
    console.log('🔍 Checking Master Admin status for typing subscription:', currentUserEmail);
    
    // Only allow Master Admin to see typing indicators
    const isMasterAdminUser = await this.isMasterAdmin(currentUserEmail);
    console.log('👑 Master Admin check result:', isMasterAdminUser);
    
    if (!isMasterAdminUser) {
      console.log('❌ Typing indicators are only available for Master Admin');
      return null;
    }
    
    console.log('✅ Setting up typing indicators subscription for Master Admin');
    const typingRef = collection(db, 'chats', chatId, 'typing');
    const typingQuery = query(typingRef, where('userId', '!=', currentUserId));
    
    console.log('📡 Firestore query path:', `chats/${chatId}/typing`);

    return onSnapshot(typingQuery, (snapshot) => {
      console.log('📥 Typing snapshot received:', {
        size: snapshot.size,
        empty: snapshot.empty,
        docs: snapshot.docs.length
      });
      
      const now = Date.now();
      const typingUsers: TypingUser[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data() as TypingUser;
        const lastActivity = data.lastActivity?.toDate()?.getTime() || 0;
        
        console.log('👤 Processing typing user:', {
          userId: data.userId,
          displayName: data.displayName,
          draftContent: data.draftContent,
          lastActivity: new Date(lastActivity),
          timeSinceActivity: now - lastActivity,
          withinTimeout: now - lastActivity < this.CLEANUP_TIMEOUT
        });
        
        // Only include users who were active within the cleanup timeout
        if (now - lastActivity < this.CLEANUP_TIMEOUT) {
          typingUsers.push(data);
        } else {
          console.log('🧹 Cleaning up stale typing indicator for:', data.userId);
          // Clean up stale typing indicators
          this.stopTyping(chatId, data.userId);
        }
      });

      console.log('📤 Sending typing users to UI:', typingUsers);
      onTypingUsersChange(typingUsers);
    }, (error) => {
      console.error('❌ Error listening to typing indicators:', error);
      onTypingUsersChange([]);
    });
  }

  /**
   * Clean up all typing indicators for a user (call on disconnect/logout)
   */
  async cleanupUserTyping(userId: string): Promise<void> {
    // This would require a more complex query to find all chats where user is typing
    // For now, we rely on the timeout mechanism
    console.log(`Cleaning up typing indicators for user: ${userId}`);
  }
}

export const typingIndicatorService = new TypingIndicatorService();
